import { Button, Container } from "@material-ui/core";
import SRCards from "../../components/SRCards";
import SelectCompany from "../../components/SelectCompany";
import { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import CommonServiceApi from "../../services/common-service";
import {
    FeatureUpdateAction,
    FeatureUpdateActionKind,
    FeatureEnabledState,
    FeaturesMetaData,
} from "./interface";
import FeatureCheckboxList from "./FeatureCheckboxList";
import UserPermissionsComponent from "./UserPermissions";
import useChangeInFeatureState from "./custom-hooks/useChangeInFeatureState";
import SnackbarDisplay from "../../components/SnackbarDisplay";
import { SnackbarDisplayProps } from "../../components/interface";
import DialogDisplay from "../../components/DialogDisplay";
import LoadingModal from "../../components/LoadingModal";
import { FeatureKeys } from "./constants";
import InvoiceCounterSettings from "../counter/InvoiceCounterSettings";

const featureAllDisabledState: FeatureEnabledState = {
    PROJ_MODULE: false,
    GBB: false,
    RECURR_VISIT: false,
    FP_FORM: false,
    MARKETING_CAMPAIGN: false,
    CLIENT_PORTAL: false,
    DYNAMIC_NOTIFICATION_TIME: false,
    TCE_FIRE_INTEGRATION: false,
    ATTENDANCE_STATUS: false,
    NEXT_VISIT_DUE: false,
    VISIT_DURATION_EDIT_ON_COMPLETE: false,
    CUST_PROSP: false,
    AUTO_SYNC_QBO: false,
    PAY_TERM_FIELD_INV_PDF: false,
    SYNC_BATC_INDV_PAY: false,
    JOB_TAGS: false,
    PAY_TERM_FIELD: false,
    RECURRING_INVOICE: false,
    OPEN_STATUS_LINE_ITEM: false,
    ATTCH_TICK_NOTES: false,
    SKIP_EQUIP_AGREMT: false,
    CUSTOM_INVOICE_PDF: false,
    CALCULATE_OVER_TIME: false,
    PROGRESSIVE_BILLING: false,
    ADVANCED_TRACKING: false,
    ESTIMATE_REJECTION_REASON: false,
    AR_DUE_DATE: false,
    PRICE_BOOK: false,
    ATTCH_PAY_LINK_DEF: false,
    ADD_LINE_ITEMS_TO_DESC: false,
    INDV_LINE_ITEM_TAX: false,
    GOOGLE_CALENDAR: false,
    PERIODIC_LOCATION_TRACKING: false,
};

const reducer = (state: FeatureEnabledState, action: FeatureUpdateAction) => {
    const { type, isChecked } = action;
    if (FeatureUpdateActionKind[type] === "ALL_FALSE") {
        return featureAllDisabledState;
    }
    return { ...state, [type]: isChecked };
};

const FeatureUpdate = () => {
    const [featureState, dispatch] = useReducer(
        reducer,
        featureAllDisabledState
    );
    const [selectedCompany, setSelectedCompany] = useState<any>(null);
    const [selectedCompanyFeatures, setSelectedCompanyFeatures] = useState<
        FeatureKeys[]
    >([]);
    const [selectedCompanyMetaData, setSelectedCompanyMetaData] =
        useState<FeaturesMetaData>({});
    const [featuresMetaData, setFeaturesMetaData] = useState<FeaturesMetaData>(
        {}
    );
    const [featuresToEnable, featuresToDisable] = useChangeInFeatureState(
        selectedCompanyFeatures,
        featureState,
        selectedCompanyMetaData,
        featuresMetaData
    );
    const [alertState, setAlertState] =
        useState<SnackbarDisplayProps["alertState"]>("");
    const [alertData, setAlertData] = useState("");
    const [dialogOpenState, setDialogOpenState] = useState(false);
    const [dialogMessage, setDialogMessage] = useState("");
    const [loadingModalState, setLoadingModalState] = useState(false);

    const apiservices = useMemo(() => new CommonServiceApi(), []);

    const handleFeatureStateChange = useCallback(
        (isChecked: boolean, featureName: string) => {
            dispatch({
                type: FeatureUpdateActionKind[
                    featureName as keyof typeof FeatureUpdateActionKind
                ],
                isChecked,
            });
        },
        [dispatch]
    );

    const postFeatureUpdateRequest = useCallback(() => {
        setLoadingModalState(false);
        setDialogOpenState(false);
        setTimeout(() => {
            setAlertState("");
        }, 6000);
    }, [setLoadingModalState, setDialogOpenState, setAlertState]);

    const confirm = useCallback(async () => {
        if (!selectedCompany || !selectedCompany.value) {
            setAlertState("error");
            setAlertData("Please select company before updating");
            postFeatureUpdateRequest();
            return;
        } else if (
            featuresToEnable.length < 1 &&
            featuresToDisable.length < 1
        ) {
            setAlertState("error");
            setAlertData(
                "Please update (or) remove any existing feature or add a new feature to proceed"
            );
            postFeatureUpdateRequest();
            return;
        }
        try {
            setLoadingModalState(true);
            const result: any =
                (await apiservices.featureUpdating({
                    featuresToEnable,
                    meta: featuresMetaData,
                    featuresToDisable,
                })) || {};
            if (
                result.settingsUpdatedList.length > 0 &&
                selectedCompany &&
                selectedCompany.value
            ) {
                let rebuildCacheResult =
                    (await apiservices.rebuildUpdatedSettingsRedisCache(
                        result.settingsUpdatedList,
                        selectedCompany.value
                    )) || [];
                getEnabledFeature();
                getFeaturesMetaData();
            }
            setAlertState("success");
            setAlertData(
                "Successfully update feature for the selected company"
            );
            postFeatureUpdateRequest();
        } catch (err: any) {
            console.error(err);
            setAlertState("error");
            setAlertData("Failed to update feature for the selected company");
            postFeatureUpdateRequest();
        }
    }, [
        featureState,
        apiservices,
        selectedCompany,
        featuresToEnable,
        featuresMetaData,
        featuresToDisable,
        handleFeatureStateChange,
        postFeatureUpdateRequest,
    ]);

    const getEnabledFeature = useCallback(async () => {
        if (selectedCompany && selectedCompany.value) {
            try {
                const result: any =
                    (await apiservices.getEnabledFeatures({
                        company: selectedCompany.value,
                    })) || "{}";
                const data = (await JSON.parse(result)) || {
                    enabledFeatures: [],
                };
                const enbledFeaturesList: FeatureKeys[] =
                    data.enabledFeatures || [];
                setSelectedCompanyFeatures(enbledFeaturesList);
                dispatch({
                    type: FeatureUpdateActionKind.ALL_FALSE,
                    isChecked: false,
                });
                for (const featureName of enbledFeaturesList) {
                    handleFeatureStateChange(true, featureName);
                }
            } catch (err: any) {
                console.error(err);
                setSelectedCompanyFeatures([]);
                dispatch({
                    type: FeatureUpdateActionKind.ALL_FALSE,
                    isChecked: false,
                });
                setAlertState("error");
                setAlertData(`${err?.description?.description ? err?.description?.description : "something went wrong please contact the admin"}`);
                postFeatureUpdateRequest();
            }
        }
    }, [
        selectedCompany,
        apiservices,
        setSelectedCompanyFeatures,
        dispatch,
        handleFeatureStateChange,
    ]);

    const getFeaturesMetaData = useCallback(async () => {
        if (selectedCompany && selectedCompany.value) {
            try {
                const result: any =
                    (await apiservices.fetchFeaturesMetaData()) || {};
                setSelectedCompanyMetaData(result);
            } catch (err: any) {
                console.error(err);
            }
        }
    }, [selectedCompany, apiservices]);

    useEffect(() => {
        getEnabledFeature();
        getFeaturesMetaData();
    }, [selectedCompany, getEnabledFeature, getFeaturesMetaData]);

    return (
        <>
            <SRCards />
            <Container>
                <h3>Feature Updating</h3>
                <SelectCompany optionHandler={setSelectedCompany} />
                <Button
                    className={"rediscache_body"}
                    variant="outlined"
                    onClick={() => {
                        if (featuresToEnable.includes("PRICE_BOOK")) {
                            setDialogMessage(
                                "⚠️ Important: If this company has Flat Rate Items that need to be available in Price Book, they must be migrated to Price Book Items BEFORE enabling Price Book. Migration will NOT be possible after Price Book is enabled."
                            );
                        } else {
                            setDialogMessage("");
                        }
                        setDialogOpenState(true);
                    }}
                    style={{ marginTop: "10px" }}
                >
                    Update
                </Button>
                <FeatureCheckboxList
                    featureState={featureState}
                    handleFeatureStateChange={handleFeatureStateChange}
                    handleFeaturesMetaDataChange={setFeaturesMetaData}
                    selectedCompany={selectedCompany}
                    selectedCompanyMetaData={selectedCompanyMetaData}
                />

                <InvoiceCounterSettings companyId={selectedCompany?.value ?? null} />

                <UserPermissionsComponent selectedCompany={selectedCompany} />

                <DialogDisplay
                    dialogOpenState={dialogOpenState}
                    dialogTitle={"Are you sure you want to update the details?"}
                    dialogMessage={dialogMessage}
                    updateDialogMessage={(message) => {
                        setDialogMessage(message);
                    }}
                    confirm={confirm}
                    setDialogOpenState={setDialogOpenState}
                />
                <SnackbarDisplay
                    alertState={alertState}
                    alertData={alertData}
                    setAlertState={setAlertState}
                />
                <LoadingModal openState={loadingModalState} />
            </Container>
        </>
    );
};

export default FeatureUpdate;
